import { useEffect, useRef, useState } from 'react';
import TenantPanel from './TenantPanel';
import OwnerPanel from './OwnerPanel';

const serviceCards = [
  {
    icon: 'ri-search-line',
    label: 'FOR TENANTS',
    title: "I'm Looking for a Home",
    description: 'Find your perfect home with ease, from shortlisting to move-in.',
    panel: 'tenant' as const,
  },
  {
    icon: 'ri-home-4-line',
    label: 'FOR OWNERS',
    title: 'I Want to Rent Out My Home',
    description: 'List your property and find reliable, verified tenants faster.',
    panel: 'owner' as const,
  },
];

export default function ServicesSection() {
  const [activePanel, setActivePanel] = useState<'tenant' | 'owner' | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollHintOpacity, setScrollHintOpacity] = useState(0);

  const updateScrollHint = () => {
    const el = scrollRef.current;
    if (!el) return;
    // Gentle "breathing" fade tied to scroll progress: the cue dissolves
    // smoothly from 100% to 0% as the user reads down the first ~120px of
    // content, and breathes back to full the moment they return toward the
    // top. Paired with a soft CSS opacity transition so it never snaps.
    const progress = Math.min(1, Math.max(0, el.scrollTop / 120));
    setScrollHintOpacity(1 - progress);
  };

  // Reset the hint when a panel opens (container starts scrolled to top).
  useEffect(() => {
    setScrollHintOpacity(1);
    const timer = window.setTimeout(updateScrollHint, 60);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanel]);

  // Lock the background scroll while a card is open so swipes don't
  // bleed through to the page behind the modal on mobile. Lock both the
  // body and the root <html> element — some mobile browsers scroll on the
  // root element, and locking only <body> lets the page slip through.
  useEffect(() => {
    if (!activePanel) return;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [activePanel]);

  const closePanel = () => setActivePanel(null);

  return (
    <>
      <section className="relative w-full bg-background-100">
        <div className="w-full px-6 md:px-12 lg:px-16 py-16 md:py-20">
          {/* Section Label */}
          <p className="text-center text-[0.7rem] tracking-[0.3em] uppercase text-accent-500 mb-12">
            OUR SERVICES
          </p>

          {/* Mobile: open row layout */}
          <div className="md:hidden flex flex-col max-w-xl mx-auto">
            {serviceCards.map((card, index) => (
              <div key={card.label}>
                <button
                  onClick={() => setActivePanel(card.panel)}
                  className="w-full flex items-center gap-4 py-8 text-left cursor-pointer group"
                >
                  {/* Icon circle — left */}
                  <div className="shrink-0 w-12 h-12 rounded-full bg-background-50 flex items-center justify-center">
                    <i className={`${card.icon} text-xl text-foreground-700`} />
                  </div>

                  {/* Text content — middle */}
                  <div className="flex-1 min-w-0">
                    <span className="text-[0.7rem] tracking-[0.25em] uppercase text-accent-600">
                      {card.label}
                    </span>
                    <h3 className="font-heading font-normal text-xl leading-snug text-foreground-950 mt-1">
                      {card.title}
                    </h3>
                  </div>

                  {/* Arrow circle — right */}
                  <div className="shrink-0 w-10 h-10 rounded-full border border-foreground-200/60 flex items-center justify-center group-hover:border-foreground-300 group-hover:bg-background-50 transition-all duration-300">
                    <i className="ri-arrow-right-line text-lg text-foreground-600 group-hover:text-foreground-900 transition-colors duration-300" />
                  </div>
                </button>

                {/* Subtle divider between rows */}
                {index < serviceCards.length - 1 && (
                  <div className="w-full h-px bg-gradient-to-r from-transparent via-foreground-200/40 to-transparent" />
                )}
              </div>
            ))}
          </div>

          {/* Desktop: cards */}
          <div className="hidden md:flex flex-col md:flex-row gap-6 md:gap-8 lg:gap-0 max-w-4xl mx-auto">
            <button
              key={serviceCards[0].label}
              onClick={() => setActivePanel(serviceCards[0].panel)}
              className="group flex-1 bg-background-50 rounded-lg border border-foreground-200/40 px-8 py-12 md:px-10 md:py-14 lg:bg-transparent lg:rounded-none lg:border-0 lg:px-20 lg:py-0 flex flex-col items-center text-center transition-all duration-300 hover:border-foreground-300/60 lg:hover:border-transparent md:hover:-translate-y-1.5 md:hover:shadow-md md:hover:scale-[1.03] lg:hover:-translate-y-1.5 lg:hover:shadow-md lg:hover:scale-[1.03] cursor-pointer"
            >
              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className={`${serviceCards[0].icon} text-3xl text-foreground-800`} />
              </div>

              {/* Label */}
              <span className="text-[0.7rem] tracking-[0.25em] uppercase text-accent-600 mb-4">
                {serviceCards[0].label}
              </span>

              {/* Main Title */}
              <h3 className="font-heading font-normal text-2xl md:text-[1.65rem] leading-snug text-foreground-950 mb-8 max-w-xs md:max-w-md whitespace-nowrap">
                {serviceCards[0].title}
              </h3>

              {/* Arrow */}
              <div className="w-10 h-10 flex items-center justify-center rounded-full border border-foreground-200/70 group-hover:border-foreground-300 group-hover:bg-background-200/40 transition-all duration-300">
                <i className="ri-arrow-right-line text-lg text-foreground-800 group-hover:text-foreground-950 transition-colors duration-300" />
              </div>
            </button>

            {/* Fading vertical divider — desktop only */}
            <div className="hidden lg:flex items-stretch">
              <div className="w-px min-h-[180px] bg-gradient-to-b from-transparent via-foreground-200/60 to-transparent" />
            </div>

            <button
              key={serviceCards[1].label}
              onClick={() => setActivePanel(serviceCards[1].panel)}
              className="group flex-1 bg-background-50 rounded-lg border border-foreground-200/40 px-8 py-12 md:px-10 md:py-14 lg:bg-transparent lg:rounded-none lg:border-0 lg:px-20 lg:py-0 flex flex-col items-center text-center transition-all duration-300 hover:border-foreground-300/60 lg:hover:border-transparent md:hover:-translate-y-1.5 md:hover:shadow-md md:hover:scale-[1.03] lg:hover:-translate-y-1.5 lg:hover:shadow-md lg:hover:scale-[1.03] cursor-pointer"
            >
              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <i className={`${serviceCards[1].icon} text-3xl text-foreground-800`} />
              </div>

              {/* Label */}
              <span className="text-[0.7rem] tracking-[0.25em] uppercase text-accent-600 mb-4">
                {serviceCards[1].label}
              </span>

              {/* Main Title */}
              <h3 className="font-heading font-normal text-2xl md:text-[1.65rem] leading-snug text-foreground-950 mb-8 max-w-xs md:max-w-md whitespace-nowrap">
                {serviceCards[1].title}
              </h3>

              {/* Arrow */}
              <div className="w-10 h-10 flex items-center justify-center rounded-full border border-foreground-200/70 group-hover:border-foreground-300 group-hover:bg-background-200/40 transition-all duration-300">
                <i className="ri-arrow-right-line text-lg text-foreground-800 group-hover:text-foreground-950 transition-colors duration-300" />
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Modal Overlay */}
      {activePanel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 backdrop-blur-sm p-6"
          onClick={closePanel}
        >
          <div
            className="relative w-[92%] md:w-[80%] max-w-[900px] bg-background-50 rounded-lg border border-foreground-200/40 shadow-[0_4px_48px_rgba(0,0,0,0.07)] animate-[modalIn_250ms_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <div
                ref={scrollRef}
                onScroll={updateScrollHint}
                className="w-full max-h-[85vh] overflow-y-auto mobile-scrollbar"
              >
                {activePanel === 'tenant' ? (
                  <TenantPanel onClose={closePanel} />
                ) : (
                  <OwnerPanel onClose={closePanel} />
                )}
              </div>

              {/* Scroll-for-more cue — phone only. Sits at the very bottom of the
                  card. The chevron is placed to the right of the label (static,
                  no bounce). The cue now breathes: it gently dissolves from full
                  to 0% opacity as you scroll down through the content, and eases
                  back in the moment you return toward the top. */}
              <div
                className={`pointer-events-none md:hidden absolute inset-x-0 bottom-0 flex items-center justify-center pb-5 pt-12 bg-gradient-to-t from-background-50 via-background-50/90 to-transparent transition-opacity duration-500 ease-out`}
                style={{ opacity: scrollHintOpacity }}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-extralight tracking-wide text-foreground-500 whitespace-nowrap">
                    Scroll for more
                  </span>
                  <i className="ri-arrow-down-s-line text-base text-foreground-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}